import React, { useCallback, useMemo, useState, ChangeEvent } from "react";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";
import NewChecklistItem from "@coworker/apprestructured/src/task-management/components/CustomTaskDescription/NewChecklistItem/NewChecklistItem";
import styles from "./CustomTaskDescription.module.css";
import ChecklistItem from "@coworker/apprestructured/src/task-management/types/ChecklistItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { useTranslation } from "@coworker/locales";

const MAX_TITLE_LENGTH = 255;
const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_CHECKLIST_ITEM_LENGTH = 255;
const MAX_CHECKLIST_ITEMS = 100;

export type CustomTaskDescriptionChange = Partial<{
  title: string;
  description: string;
  checklist: ChecklistItem[];
}>;

type CustomTaskDescriptionProps = {
  title: string;
  description: string;
  checklist: ChecklistItem[];
  disabled: boolean;
  showValidation: boolean;
  onConfirmClick: ({
    title,
    description,
    checklist,
  }: CustomTaskDescriptionChange) => void;
};

export function CustomTaskDescription({
  title,
  description,
  checklist,
  disabled,
  showValidation,
  onConfirmClick,
}: CustomTaskDescriptionProps) {
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(title);
  const [modalDescription, setModalDescription] = useState(description);
  const [modalChecklist, setModalChecklist] = useState(checklist);
  const [modalChecklistInput, setModalChecklistInput] = useState("");
  const [hasClickedConfirmWithEmptyTitle, setHasClickedConfirmWithEmptyTitle] =
    useState(false);

  const { t } = useTranslation();

  const titleError = useMemo(() => {
    if (!modalTitle && (hasClickedConfirmWithEmptyTitle || showValidation)) {
      return t("pleaseFillInString");
    }
    if (modalTitle.length > MAX_TITLE_LENGTH) {
      return t("maximumCharactersLengthString", { count: MAX_TITLE_LENGTH });
    }
    return null;
  }, [hasClickedConfirmWithEmptyTitle, modalTitle, showValidation, t]);

  const descriptionError = useMemo(() => {
    if (modalDescription.length > MAX_DESCRIPTION_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_DESCRIPTION_LENGTH,
      });
    }
    return null;
  }, [modalDescription, t]);

  const checklistInputError = useMemo(() => {
    if (modalChecklistInput.length > MAX_CHECKLIST_ITEM_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_CHECKLIST_ITEM_LENGTH,
      });
    }
    return null;
  }, [modalChecklistInput, t]);

  const listViewError = useMemo(() => {
    if (!title && showValidation) {
      return t("pleaseFillInString");
    }
    return null;
  }, [showValidation, t, title]);

  const {
    saveGuardedFunction: handleCloseModalClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setHasClickedConfirmWithEmptyTitle(false);
    }, [])
  );

  const handleConfirmClick = useCallback(() => {
    setHasClickedConfirmWithEmptyTitle(!modalTitle);
    if (
      modalTitle &&
      !titleError &&
      !descriptionError &&
      !checklistInputError
    ) {
      const change: CustomTaskDescriptionChange = {};
      if (modalTitle !== title) {
        change.title = modalTitle;
      }
      if (modalDescription !== description) {
        change.description = modalDescription;
      }
      if (modalChecklist !== checklist) {
        change.checklist = modalChecklist;
      }

      disableSaveGuard();
      onConfirmClick(change);
      setShowModal(false);
    }
  }, [
    checklist,
    description,
    disableSaveGuard,
    modalChecklist,
    modalDescription,
    modalTitle,
    onConfirmClick,
    title,
    titleError,
    descriptionError,
    checklistInputError,
  ]);

  const addChecklistItem = useCallback(() => {
    if (
      modalChecklistInput &&
      modalChecklist.length < MAX_CHECKLIST_ITEMS &&
      !checklistInputError
    ) {
      setModalChecklist((prev) => [
        ...prev,
        { title: modalChecklistInput, completed: false },
      ]);
      setModalChecklistInput("");
      enableSaveGuard();
    }
  }, [
    enableSaveGuard,
    modalChecklistInput,
    modalChecklist.length,
    checklistInputError,
  ]);

  const handleAddChecklistClick = useCallback(() => {
    addChecklistItem();
  }, [addChecklistItem]);

  const handleDeleteChecklistItem = useCallback(
    (index: number) => {
      setModalChecklist((prev) => prev.filter((_, i) => i !== index));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleChecklistInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        addChecklistItem();
      }
    },
    [addChecklistItem]
  );

  const handleListItemClick = useCallback(() => {
    setModalTitle(title);
    setModalDescription(description);
    setModalChecklist(checklist);
    setModalChecklistInput("");
    setShowModal(true);
  }, [checklist, description, title]);

  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setModalTitle(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setModalDescription(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleChecklistInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setModalChecklistInput(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  return (
    <>
      <ListItem
        onClick={handleListItemClick}
        title={t("taskDescriptionString")}
        disabled={disabled}
        description={title}
        addOn={listViewError ? <ErrorAddOn>{listViewError}</ErrorAddOn> : null}
        emphasised={title.length > 0}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModalClick}>
        <FixaSheets
          labelledById="task-management-custom-task-description-header"
          header={
            <FixaModalHeader
              id="task-management-custom-task-description-header"
              closeBtnClick={handleCloseModalClick}
              title={t("taskDescriptionString")}
            />
          }
          footer={
            <StyledModalFooter>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                fluid
                onClick={handleConfirmClick}
              />
            </StyledModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <ModalColumn gap="medium">
              <InputWrapper error={titleError}>
                <FixaInputField
                  id="title"
                  type="text"
                  label={t("taskTitleString")}
                  value={modalTitle}
                  disabled={disabled}
                  onChange={handleTitleChange}
                />
              </InputWrapper>
              <InputWrapper error={descriptionError}>
                <FixaTextArea
                  id="description"
                  label={t("description4String")}
                  value={modalDescription}
                  disabled={disabled}
                  onChange={handleDescriptionChange}
                />
              </InputWrapper>
              <ModalColumn gap="small">
                <InputWrapper error={checklistInputError}>
                  <FixaInputField
                    id="checklist-input"
                    type="text"
                    label={t("createChecklistString")}
                    value={modalChecklistInput}
                    disabled={disabled}
                    onChange={handleChecklistInputChange}
                    onKeyDown={handleChecklistInputKeyDown}
                  />
                </InputWrapper>
                <FixaButton
                  className={styles["addButton"]}
                  text={t("addString")}
                  onClick={handleAddChecklistClick}
                  size="xsmall"
                  type="emphasised"
                  disabled={
                    disabled || modalChecklist.length >= MAX_CHECKLIST_ITEMS
                  }
                />
              </ModalColumn>
              <div className={styles["checklistItemsWrapper"]}>
                {modalChecklist.map(({ title }, index) => {
                  return (
                    <NewChecklistItem
                      key={index}
                      title={title}
                      onDelete={() => handleDeleteChecklistItem(index)}
                    />
                  );
                })}
              </div>
            </ModalColumn>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}
